import { useEffect, useMemo, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@mds/shared/lib/api';
import {
  RATE_CARD_UNITS,
  type AdminRateCard,
  type DeskPackageConfiguration,
  type RateCardItem,
  type RateCardUnit,
} from '@mds/shared/types/rate-card';

import { useSession } from '@/auth/use-session';
import PackageEditor from '@/components/rate-card/package-editor';
import RateTable, { type DraftItem, type Group } from '@/components/rate-card/rate-table';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import Field, { inputCls } from '@/components/ui/field';
import Modal from '@/components/ui/modal';
import Tabs from '@/components/ui/tabs';
import {
  PACKAGE_UNITS,
  defaultPackage,
  isPackageUnit,
  rateCardSchema,
  savedAccessOf,
} from '@/domain/packages';
import { centsToInput, inputToCents } from '@/lib/money';
import { stamp } from '@mds/shared/lib/dates';
import { useValidation, type FieldBinder } from '@/lib/validation';
import * as rateCard from '@/rate-card/rate-card.api';
import * as resources from '@/resources/resources.api';

const GROUPS: { key: Group; title: string; hint: string }[] = [
  {
    key: 'spaceRates',
    title: 'Space rates',
    hint: 'What each bookable space costs. Resources reference these, so one cannot be removed or switched to an incompatible unit while something is priced off it.',
  },
  { key: 'deskPackages', title: 'Desk packages', hint: 'What a coworking customer can buy.' },
  { key: 'addOns', title: 'Add-ons', hint: 'Extras billed on top of a space or package.' },
];

/** "Meeting & Training Room" → "meeting-training-room". */
const slug = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);

const toDraft = (
  items: Array<RateCardItem & { package?: DeskPackageConfiguration }>,
): DraftItem[] =>
  items.map((item) => ({
    id: item.id,
    name: item.name,
    amount: centsToInput(item.amountMinor),
    unit: item.unit,
    note: item.note,
    isNew: false,
    ...(item.package ? { package: item.package } : {}),
  }));

/** A desk package row's rules, or the defaults a row without any starts from. */
const rulesOf = (item: DraftItem) =>
  item.package ?? defaultPackage(isPackageUnit(item.unit) ? item.unit : 'day');

type Draft = Record<Group, DraftItem[]>;

const EMPTY: Draft = { spaceRates: [], deskPackages: [], addOns: [] };

/** One resource the replacement would leave unpriced, named rather than keyed. */
interface Blocked {
  id: string;
  resource: string;
  rate: string;
  issue: 'missing_rate' | 'incompatible_unit';
  expectedUnit: string;
}

const draftOf = (card: AdminRateCard): Draft => ({
  spaceRates: toDraft(card.spaceRates),
  deskPackages: toDraft(card.deskPackages),
  addOns: toDraft(card.addOns),
});

/**
 * Gives every row added this session an identifier of its own, named after the
 * row. Existing rows keep theirs untouched, so renaming one does not break the
 * resources and bookings that point at it.
 */
function withIds(draft: Draft): Draft {
  const taken = new Set(
    GROUPS.flatMap(({ key }) => draft[key].filter((i) => !i.isNew).map((i) => i.id)),
  );
  const next = {} as Draft;
  for (const { key } of GROUPS) {
    next[key] = draft[key].map((item) => {
      if (!item.isNew) return item;
      const base = slug(item.name) || 'rate';
      let id = base;
      for (let n = 2; taken.has(id); n += 1) id = `${base}-${n}`;
      taken.add(id);
      return { ...item, id };
    });
  }
  return next;
}

/** Draft rows as the API wants them: trimmed, in cents, without sortOrder. */
const toRequest = (items: DraftItem[]) =>
  items.map((item) => ({
    id: item.id,
    name: item.name.trim(),
    amountMinor: inputToCents(item.amount) ?? 0,
    unit: item.unit,
    note: item.note.trim(),
  }));

/**
 * Desk packages carry their complete rules, trimmed. The API keeps an existing
 * package's rules when they are omitted, but this editor shows them, so it
 * always sends what it shows.
 */
const toPackageRequest = (items: DraftItem[]) =>
  items.map((item) => {
    const rules = rulesOf(item);
    return {
      ...toRequest([item])[0]!,
      package: {
        ...rules,
        eligibility:
          rules.eligibility.kind === 'none'
            ? { kind: 'none' as const, instructions: '' }
            : { kind: rules.eligibility.kind, instructions: rules.eligibility.instructions.trim() },
        benefits: rules.benefits.map((b) => ({
          id: b.id,
          title: b.title.trim(),
          description: b.description.trim(),
        })),
      },
    };
  });

/**
 * The rate card.
 *
 * The API publishes a whole replacement version at a time, so this is a draft
 * of all three lists with one Publish, not a grid of per-row saves. An omitted
 * row is a deliberate deletion, and the version number the draft was taken
 * from goes back with it so a concurrent publish is caught rather than
 * silently overwritten.
 */
export default function PackagesPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const mayManage = can('facilities.manage');

  const card = useQuery({ queryKey: ['rate-card'], queryFn: rateCard.getRateCard });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [changeSummary, setChangeSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const [stranded, setStranded] = useState<Blocked[]>([]);
  const [conflict, setConflict] = useState<{
    expectedVersionNumber: number;
    currentVersionNumber: number;
  } | null>(null);
  const [published, setPublished] = useState<number | null>(null);
  /** The desk package whose rules are open, by its row index. */
  const [rulesFor, setRulesFor] = useState<number | null>(null);
  const checked = useMemo(() => ({ changeSummary, ...(draft ?? EMPTY) }), [changeSummary, draft]);
  // Saved access times may predate the 8 AM – midnight limit and stay as they are.
  const schema = useMemo(() => rateCardSchema(savedAccessOf(card.data)), [card.data]);
  const validation = useValidation(schema, checked);
  const [tab, setTab] = useState<Group>('spaceRates');
  /** Set once Publish is pressed, so a tab hiding a problem says so. */
  const [attempted, setAttempted] = useState(false);
  const failing = (group: Group) =>
    Object.keys(validation.errors).some((path) => path.startsWith(`${group}.`));

  // Seed the draft the first time the card arrives. Later refetches leave an
  // in-progress edit alone; Discard is how a draft is thrown away.
  useEffect(() => {
    if (card.data && draft === null) setDraft(draftOf(card.data));
  }, [card.data, draft]);

  const dirty = useMemo(() => {
    if (!card.data || !draft) return false;
    return JSON.stringify(draft) !== JSON.stringify(draftOf(card.data));
  }, [card.data, draft]);

  const setGroup = (group: Group, items: DraftItem[]) =>
    setDraft((cur) => (cur ? { ...cur, [group]: items } : cur));

  const reset = (from?: AdminRateCard) => {
    const source = from ?? card.data;
    if (source) setDraft(draftOf(source));
    setChangeSummary('');
    setProblems([]);
    setStranded([]);
    setConflict(null);
    setAttempted(false);
    validation.reset();
  };

  const reload = async () => {
    setBusy(true);
    try {
      const fresh = await queryClient.fetchQuery({
        queryKey: ['rate-card'],
        queryFn: rateCard.getRateCard,
      });
      reset(fresh);
      setPublished(null);
    } finally {
      setBusy(false);
    }
  };

  /**
   * The API reports the block by resource and rate key. Only the inventory
   * knows what those are called, and only on this rare path — so it is fetched
   * here rather than loaded on every visit. The rate is named from the card
   * being replaced, which still holds the row the draft dropped.
   */
  const namesFor = async (blocked: rateCard.StrandedResource[]): Promise<Blocked[]> => {
    const rateName = (id: string) =>
      card.data?.spaceRates.find((r) => r.id === id)?.name ??
      'a rate that is no longer on the card';

    let named = new Map<string, string>();
    try {
      const inventory = await queryClient.fetchQuery({
        queryKey: ['resources', { includeInactive: true }],
        queryFn: () => resources.listResources({ includeInactive: true }),
      });
      named = new Map(inventory.map((r) => [r.slug, r.name]));
    } catch {
      // Naming them is a courtesy; the block itself still has to be reported.
    }

    return blocked.map((b) => ({
      id: b.resourceId,
      resource: named.get(b.resourceSlug) ?? b.resourceSlug,
      rate: rateName(b.rateItemId),
      issue: b.issue,
      expectedUnit: b.expectedUnit,
    }));
  };

  const publish = async () => {
    if (!card.data || !draft) return;
    setAttempted(true);
    // Land on the first tab with a problem before checking, so the focus
    // `check()` moves lands on a field that is drawn.
    const first = GROUPS.find(({ key }) => failing(key));
    if (first && !failing(tab)) setTab(first.key);
    if (!validation.check()) return;
    setProblems([]);
    setStranded([]);
    setConflict(null);
    setPublished(null);

    const resolved = withIds(draft);

    setBusy(true);
    try {
      const next = await rateCard.replaceRateCard({
        expectedVersionNumber: card.data.versionNumber,
        changeSummary: changeSummary.trim() === '' ? null : changeSummary.trim(),
        spaceRates: toRequest(resolved.spaceRates),
        deskPackages: toPackageRequest(resolved.deskPackages),
        addOns: toRequest(resolved.addOns),
      });
      queryClient.setQueryData(['rate-card'], next);
      reset(next);
      setPublished(next.versionNumber);
    } catch (e) {
      if (!(e instanceof ApiError)) {
        setProblems(['The server could not be reached. Nothing was published.']);
        return;
      }
      const details = e.body ? (e.body as { details?: unknown }).details : null;
      const clash = rateCard.versionConflict(details);
      const blocked = rateCard.strandedResources(details);
      if (clash) setConflict(clash);
      else if (blocked.length > 0) {
        setStranded(await namesFor(blocked));
        setTab('spaceRates');
      }
      else setProblems([e.message]);
    } finally {
      setBusy(false);
    }
  };

  if (card.isPending) {
    return <Card title="Rate card">Loading the rate card…</Card>;
  }
  if (card.isError || !card.data || !draft) {
    return (
      <EmptyState
        title="Could not load the rate card"
        hint={
          card.error instanceof ApiError ? card.error.message : 'The server could not be reached.'
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Card title={`Version ${card.data.versionNumber}`}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Chip>Activated {stamp(card.data.activatedAt)}</Chip>
            {dirty && (
              <Chip className="border border-warn-edge bg-warn-wash text-warn-text">
                Unpublished changes
              </Chip>
            )}
          </div>

          <p className="m-0 text-base text-ink-muted">
            Publishing writes a new immutable version. Existing bookings keep the rate they were
            made at; only new quotes use the new one. A row you remove here is gone from the next
            version.
          </p>

          {card.data.changeSummary && (
            <p className="m-0 text-base text-ink-secondary">
              Current version: {card.data.changeSummary}
            </p>
          )}

          {mayManage ? (
            <>
              <Field
                label="What changed"
                hint="Optional. Recorded against the new version."
                {...validation.field('changeSummary')}
              >
                <input
                  className={inputCls}
                  value={changeSummary}
                  onChange={(e) => setChangeSummary(e.target.value)}
                  placeholder="September pricing review"
                />
              </Field>

              <div className="flex flex-wrap items-center gap-3">
                <Button onClick={() => void publish()} disabled={busy || !dirty}>
                  {busy ? 'Publishing…' : 'Publish new version'}
                </Button>
                <Button variant="quiet" disabled={busy || !dirty} onClick={() => reset()}>
                  Discard changes
                </Button>
                {published !== null && (
                  <span className="font-mono text-sm tracking-wider text-brand-text uppercase">
                    Published v{published}
                  </span>
                )}
              </div>
            </>
          ) : (
            <p className="m-0 text-base text-ink-muted">
              You can read the rate card but not change it. Ask a super administrator if you need to
              set prices.
            </p>
          )}

          {conflict && (
            <div
              role="alert"
              className="flex flex-col gap-3 rounded-xl border border-warn-edge bg-warn-wash px-4 py-3 text-base text-warn-text"
            >
              <span>
                Another administrator published while you were editing. The card moved from version{' '}
                {conflict.expectedVersionNumber} to {conflict.currentVersionNumber}. Nothing of
                yours was saved.
              </span>
              <span className="text-base">
                Reload to see their version, then reapply whatever is still needed. Publishing over
                the top would quietly undo their change.
              </span>
              <div>
                <Button variant="quiet" disabled={busy} onClick={() => void reload()}>
                  Reload and discard my changes
                </Button>
              </div>
            </div>
          )}

          {stranded.length > 0 && (
            <div
              role="alert"
              className="flex flex-col gap-2 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text"
            >
              <span>
                Nothing was published. These resources are priced off space rates the replacement
                would break. Change the inventory first, then publish.
              </span>
              <ul className="m-0 flex list-none flex-col gap-1 p-0 text-base">
                {stranded.map((r) => (
                  <li key={r.id}>
                    <strong className="font-semibold">{r.resource}</strong> is priced off {r.rate},
                    which this change{' '}
                    {r.issue === 'missing_rate'
                      ? 'removes'
                      : `no longer charges by the ${r.expectedUnit}`}
                    .
                  </li>
                ))}
              </ul>
            </div>
          )}

          {problems.length > 0 && (
            <ul
              role="alert"
              className="m-0 flex list-none flex-col gap-1 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text"
            >
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      <div className="flex flex-col gap-4">
        <Tabs
          label="Rate card sections"
          tabs={GROUPS.map(({ key, title }) => ({ key, label: title }))}
          active={tab}
          onPick={setTab}
          flagged={(key) => attempted && failing(key)}
        />
        {GROUPS.filter(({ key }) => key === tab).map(({ key, title, hint }) => (
          <RateTable
            key={key}
            title={title}
            hint={hint}
            group={key}
            rows={draft[key]}
            units={
              key === 'deskPackages'
                ? (PACKAGE_UNITS as readonly RateCardUnit[])
                : (RATE_CARD_UNITS as readonly RateCardUnit[])
            }
            editable={mayManage}
            onChange={(rows) => setGroup(key, rows)}
            onEditPackage={key === 'deskPackages' ? setRulesFor : undefined}
            field={validation.field}
          />
        ))}
      </div>

      <RulesModal
        row={rulesFor !== null ? draft.deskPackages[rulesFor] : undefined}
        editable={mayManage}
        field={(path, options) =>
          validation.field(`deskPackages.${rulesFor}.package.${path}`, options)
        }
        onClose={() => {
          // Leaving the rules counts as leaving the field, so the row says
          // straight away when something in them still needs fixing.
          if (rulesFor !== null) validation.touch(`deskPackages.${rulesFor}.package`);
          setRulesFor(null);
        }}
        onChange={(next) =>
          setGroup(
            'deskPackages',
            draft.deskPackages.map((r, i) => (i === rulesFor ? { ...r, package: next } : r)),
          )
        }
      />
    </div>
  );
}

/** One desk package's rules, edited in place on the draft card. */
function RulesModal({
  row,
  editable,
  field,
  onClose,
  onChange,
}: {
  row: DraftItem | undefined;
  editable: boolean;
  /** The page's validation, with paths relative to this row's rules. */
  field: FieldBinder;
  onClose: () => void;
  onChange: (next: DeskPackageConfiguration) => void;
}) {
  const rules = row ? rulesOf(row) : null;
  return (
    <Modal
      open={row !== undefined}
      onClose={onClose}
      title={`${row?.name.trim() || 'New package'} · rules`}
      footer={<Button onClick={onClose}>Done</Button>}
    >
      {row && rules && (
        <>
          <PackageEditor
            value={rules}
            unit={isPackageUnit(row.unit) ? row.unit : 'day'}
            readOnly={!editable}
            onChange={onChange}
            field={field}
          />
          <p className="m-0 text-base text-ink-muted">
            Nothing is saved until the card is published. Bookings already made keep the rules they
            were sold with.
          </p>
        </>
      )}
    </Modal>
  );
}
